/* eslint-disable camelcase */
/* eslint-disable no-param-reassign */
import React, { Suspense, useContext, useEffect, useState } from 'react';
import { AuthContext } from '@xometry/auth-context';
import { Box, theme, Link, Flex, Spinner } from '@xometry/xometry_loft';
import { css } from '@emotion/react';
import { useQuery } from '@apollo/client';
import {
  API_ORIGIN,
  AUTH_ORIGIN,
  PARTNER_ORIGIN,
  WWW_ORIGIN,
} from '../../../config';
import helpers, { getPrimaryPartnerId } from '../../../utils/helpers';
import { FEATURE_FLAGS, useFeatureFlag } from '../../../hooks';
import { lazyWithRetries } from '../../../utils/shimport';
import InvitationAlert from './InvitationAlert';
import {
  UserInvitationsQuery,
  UserInvitationsQueryResult,
} from '../../../schema/generated/schema';

const registrationFormLink = `${WWW_ORIGIN}/supplier-creation/quoting/components/supplier-creation-form/component.mjs`;
const SupplierCreationForm = lazyWithRetries(registrationFormLink);

export const RedirectToPartnerOnboarding = () => {
  useEffect(() => {
    window.location.replace(`${PARTNER_ORIGIN}/supplier-onboarding`);
  }, []);
  return null;
};

const SupplierRegistration = () => {
  const isAddressMFEEnabled = useFeatureFlag(FEATURE_FLAGS.AddressMFE);
  const { isLoading, user } = useContext(AuthContext);

  const { data: invitationsData, loading: invitationsLoading } =
    useQuery<UserInvitationsQueryResult>(UserInvitationsQuery);
  const [skipInvitationQuery, setSkipInvitationQuery] = useState(false);

  useEffect(() => {
    if (!invitationsLoading) {
      return () => {};
    }

    // Defensive programming - if the query is taking too long, skip it
    const timeout = setTimeout(() => {
      setSkipInvitationQuery(true);
    }, 10000);

    return () => clearTimeout(timeout);
  }, [invitationsLoading]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (!user) {
      window.location.assign('/login');
    }

    if (window.analytics) window.analytics.page('Supplier Registration');
    const url = new URL(window.location.toString());
    // Grabs the URL search params
    const redirectParam = url.searchParams.get('redirect') || '';
    const domainParam = url.searchParams.get('domain') || '';
    // Replaces redirect with space, and then goes to helper to remove extra slashes
    const redirectPath = helpers.parseRedirectQueryPath(
      decodeURIComponent(redirectParam)
    );
    const redirectDomain =
      helpers.parseRedirectQueryPath(decodeURIComponent(domainParam)) || 'www';

    async function checkUserStatus() {
      if (getPrimaryPartnerId(user)) {
        if (redirectPath) {
          window.location.assign(redirectPath);
        } else {
          window.location.href = '/account/register-shop-finances';
        }
      }
    }
    checkUserStatus();
    /**
     * Redirect to the next screen in registration
     */
    const handleSupplierRegisterSuccess = () => {
      if (redirectParam) {
        window.location.assign(
          `${WWW_ORIGIN}/refresh-tokens?domain=${redirectDomain}&destination=${redirectPath}`
        );
      } else {
        window.location.assign(
          `${WWW_ORIGIN}/refresh-tokens?&domain=${redirectDomain}&destination=/account/supplier/onboarding`
        );
      }
    };

    // Bind the event listener
    document.addEventListener(
      'registerSupplierSuccess',
      handleSupplierRegisterSuccess
    );
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener(
        'registerSupplierSuccess',
        handleSupplierRegisterSuccess
      );
    };
  }, [isLoading]);

  if (isLoading || (invitationsLoading && !skipInvitationQuery)) {
    return <Spinner />;
  }

  return (
    <Flex
      css={css`
        box-shadow: 0px 1px 4px rgba(37, 56, 66, 0.2);
      `}
      backgroundColor={theme.colors.white}
      margin={['0 auto', '40px auto']}
      display="table"
      padding={['20px', '20px 40px']}
      maxWidth="700px"
    >
      <Box textAlign="center">
        <h3
          css={css`
            color: ${theme.colors.darkBlues[100]};
            font-weight: ${theme.fontWeights.regular};
            font-size: 28px;
            font-family: 'Open Sans';
            font-style: normal;
            margin-top: 0px;
            padding-top: 24px;
            margin-bottom: 0px;
            line-height: 36px;

            ${theme.mediaQueries.small} {
              font-size: 28px;
              margin-top: initial;
            }
          `}
        >
          Join Our Network
        </h3>
        <Box
          css={css`
            font-weight: ${theme.fontWeights.regular};
            font-style: normal;
            color: ${theme.colors.darkBlues[75]};
          `}
          fontFamily={theme.fonts[0]}
          fontSize={theme.fontSizes[0]}
          lineHeight={theme.lineHeights[2]}
          maxWidth="524px"
          marginTop="8px"
        >
          Register today to become part of the largest marketplace for custom
          manufacturing.
        </Box>
      </Box>
      <Box
        marginTop="32px"
        css={css`
          fieldset {
            box-sizing: border-box;
            margin-bottom: 2px;
            padding: 4px 0;
          }
          button {
            padding: 12px 24px;
          }
        `}
      >
        {invitationsData?.userInvitations[0] && (
          <Box marginBottom="24px">
            <InvitationAlert invitation={invitationsData.userInvitations[0]} />
          </Box>
        )}
        <Suspense fallback={<Spinner />}>
          <SupplierCreationForm
            API_ORIGIN={API_ORIGIN}
            AUTH_ORIGIN={AUTH_ORIGIN}
            WWW_ORIGIN={WWW_ORIGIN}
            isAddressMFEEnabled={isAddressMFEEnabled}
            submitButtonText="Continue Registration"
            onboardingOrigin="Marketplace"
          />
        </Suspense>
      </Box>
      <Box
        css={css`
          font-weight: ${theme.fontWeights.regular};
          font-style: normal;
          color: ${theme.colors.darkBlues[90]};
        `}
        fontSize={theme.fontSizes[0]}
        lineHeight={theme.lineHeights[2]}
        maxWidth="520px"
        marginTop="32px"
        marginBottom="16px"
      >
        By joining the Xometry Manufacturing Network, you agree to our{' '}
        <Link href="https://www.xometry.com/partner-terms-and-conditions/">
          Partner Terms and Conditions
        </Link>
        ,{' '}
        <Link href="https://xometry-marketing.cdn.prismic.io/xometry-marketing/46b8d3fc-8a69-4a3a-ad40-c2c10a83307d_Xometry+Privacy+Policy_05272022.pdf">
          Privacy Policy
        </Link>
        , and{' '}
        <Link href="https://xometry-marketing.cdn.prismic.io/xometry-marketing/2c6df185-2314-4bfc-afc4-9a3300933e41_%5B03.01.2023%5D+Xometry+Mutual+Partner+NDA.pdf">
          Non Disclosure agreement
        </Link>
        :
      </Box>
    </Flex>
  );
};

export default SupplierRegistration;
